import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import CircularProgress from "@mui/material/CircularProgress";
import toast from "react-hot-toast";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { CompanyService } from "services/companyService";
import { GeneralJournalService } from "services/general-journal";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  month: (today.getMonth() + 1).toString(),
  year: today.getFullYear().toString(),
  creditAccountLabels: [],
  rows: [],
  totals: {
    creditValues: [],
    totalDebit112: 0,
  },
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function AccountingTable2Detail() {
  const location = useLocation();
  const navigation = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialValues);
  const [companyInfo, setCompanyInfo] = useState({ name: "", address: "" });

  const queryParams = new URLSearchParams(location.search);
  const filters = {
    year: queryParams.get("year"),
    fromDate: queryParams.get("fromDate"),
    toDate: queryParams.get("toDate"),
    accountCode: queryParams.get("accountCode"),
    searchBy: queryParams.get("searchBy"),
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await CompanyService.getByAuth();
        const payload = response?.data || response || {};
        setCompanyInfo({
          name: payload.name || "",
          address: payload.address || "",
        });
      } catch (error) {
        console.error("Lỗi lấy thông tin công ty:", error);
      }
    };
    fetchCompany();
  }, []);

  const fetchData = useCallback(async () => {
    if (!filters.year || !filters.accountCode) return;

    try {
      setLoading(true);
      const response = await GeneralJournalService.getCashReceiptJournal({
        company: "me", // Backend will use auth user's company
        year: Number(filters.year),
        accountCode: filters.accountCode,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        searchBy: filters.searchBy || undefined,
        paginate: false,
      });

      const payload = response?.data || response || {};
      const trackedAccount = payload.trackedAccount || {};
      const detailColumns = payload.detailColumns || [];
      const items = payload.items || [];
      const summary = payload.summary || {};

      const reportDate = `${today.getDate().toString().padStart(2, "0")}/${(today.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${today.getFullYear()}`;

      setData({
        unit: companyInfo.name,
        address: companyInfo.address,
        month: filters.fromDate ? filters.fromDate.split("-")[1] : (today.getMonth() + 1).toString(),
        year: filters.year,
        creditAccountLabels: detailColumns.map((col) => col.accountCode),
        rows: items.map((item, idx) => ({
          index: idx + 1,
          voucherNumber: item.voucherNumber || "",
          voucherDate: item.voucherDate,
          description: item.description || "",
          creditValues: detailColumns.map((col) => item.detailAmounts?.[col.key] || 0),
          totalDebit112: item.debitAmount || 0,
        })),
        totals: {
          creditValues: detailColumns.map((col) => summary.totalDetailAmounts?.[col.key] || 0),
          totalDebit112: summary.totalDebitAmount || 0,
        },
        reportDate: reportDate,
        signingDay: today.getDate().toString(),
        signingMonth: (today.getMonth() + 1).toString(),
        signingYear: today.getFullYear().toString(),
        trackedAccount: trackedAccount,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải dữ liệu Bảng kê số 2");
    } finally {
      setLoading(false);
    }
  }, [filters.year, filters.accountCode, filters.fromDate, filters.toDate, filters.searchBy, companyInfo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBack = () => navigation("/templates/accounting-table-2");

  const handlePrint = () => {
    const html = getPrintTemplate(data);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S04-BK2</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                {loading ? (
                  <SoftBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </SoftBox>
                ) : (
                  <>
                    <SoftBox mb={3} textAlign="center">
                      <SoftTypography variant="h5" fontWeight="bold">
                        BẢNG KÊ SỐ 2
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        (Dùng cho tài khoản {data.trackedAccount?.accountCode || filters.accountCode})
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        Từ ngày {filters.fromDate || "..."} đến ngày {filters.toDate || "..."}
                      </SoftTypography>
                    </SoftBox>

                    <SoftBox display="flex" justifyContent="center" p={5}>
                      <SoftTypography variant="body1">
                        Dữ liệu đã được tải tự động. Nhấn "In Bảng" để xem bản in chuẩn Thông tư 99/2025/TT-BTC.
                      </SoftTypography>
                    </SoftBox>

                    <SoftBox display="flex" justifyContent="flex-end" mt={3} gap={2}>
                      <SoftButton variant="gradient" color="light" onClick={handleBack}>
                        Quay lại
                      </SoftButton>
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    </SoftBox>
                  </>
                )}
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AccountingTable2Detail;
